/**
 * CountryFlag — cross-platform flag renderer.
 *
 * Windows Chrome/Edge don't ship regional-indicator emoji glyphs, so 🇦🇺
 * shows up as "AU". This component maps a country name/slug/emoji to an
 * SVG flag served by flagcdn.com so flags render identically on Windows,
 * macOS, Linux and mobile.
 *
 * Usage:
 *   <CountryFlag country="Australia" />
 *   <CountryFlag country="au" size="lg" />
 *   <CountryFlag emoji="🇦🇺" alt="Australia" />
 */

const NAME_TO_CODE = {
  australia: 'au', canada: 'ca', dubai: 'ae', uae: 'ae', 'united arab emirates': 'ae',
  france: 'fr', germany: 'de', ireland: 'ie', malaysia: 'my', 'new zealand': 'nz',
  singapore: 'sg', uk: 'gb', 'united kingdom': 'gb', 'great britain': 'gb',
  usa: 'us', 'united states': 'us', 'united states of america': 'us',
  finland: 'fi', hungary: 'hu', india: 'in', china: 'cn', japan: 'jp',
  'south korea': 'kr', korea: 'kr', italy: 'it', spain: 'es', netherlands: 'nl',
  sweden: 'se', norway: 'no', denmark: 'dk', switzerland: 'ch', poland: 'pl',
  russia: 'ru',
};

// Regional-indicator emoji → ISO code
const emojiToCode = (emoji) => {
  if (!emoji || typeof emoji !== 'string') return null;
  const chars = [...emoji];
  if (chars.length < 2) return null;
  const a = chars[0].codePointAt(0);
  const b = chars[1].codePointAt(0);
  if (a < 0x1F1E6 || a > 0x1F1FF || b < 0x1F1E6 || b > 0x1F1FF) return null;
  return String.fromCharCode(65 + (a - 0x1F1E6)) + String.fromCharCode(65 + (b - 0x1F1E6));
};

const SIZE_MAP = {
  xs: 'w-4 h-3',
  sm: 'w-5 h-4',
  md: 'w-6 h-4',
  lg: 'w-8 h-6',
  xl: 'w-12 h-8',
};

const CountryFlag = ({ country, emoji, size = 'md', className = '', alt }) => {
  let code = null;
  if (country) {
    const key = String(country).toLowerCase().trim();
    code = NAME_TO_CODE[key] || (key.length === 2 ? key : null);
  }
  if (!code && emoji) {
    code = emojiToCode(emoji)?.toLowerCase();
  }
  if (!code) {
    // Graceful fallback — render the raw emoji even if it may show as text
    // on Windows; better than a blank space.
    return <span className={className}>{emoji || ''}</span>;
  }
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
  return (
    <img
      src={`https://flagcdn.com/w80/${code}.png`}
      srcSet={`https://flagcdn.com/w160/${code}.png 2x`}
      alt={alt || country || code}
      loading="lazy"
      className={`inline-block object-cover rounded-sm shadow-sm ${sizeClass} ${className}`}
    />
  );
};

export default CountryFlag;
